/* ==========================================================================
   Lockett Labs TCG picker behaviour (sections/ll-tcg-picker.liquid)

   - Arrival: the section's inline script arms a one-time rise when the section starts
     below the fold; this shows it when it scrolls in (and at once if this file is late).
   - Off screen, the active card's effects pause (.is-idle).
   - Phones: a carousel. The centred card is the active one (.is-active, and .is-pop while
     the carousel is on screen), with arrows, progress bars, keyboard focus and mouse drag.
   - Tablets without hover keep the grid; the row crossing the middle of the screen pops.
   Nothing here runs per frame except one rAF after a carousel scroll.
   ========================================================================== */
(function () {
  'use strict';

  function boot(root) {
    if (!root || root.dataset.gpBooted === '1') return;
    root.dataset.gpBooted = '1';
    var cleanups = [];
    var hasIO = 'IntersectionObserver' in window;
    var mm = window.matchMedia ? function (q) { return window.matchMedia(q); } : function () { return { matches: false }; };
    var phone = mm('(max-width: 699px)');
    var noHover = mm('(hover: none)');
    var reduce = mm('(prefers-reduced-motion: reduce)').matches;
    var list = root.querySelector('[data-gp-list]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-gp-item]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-gp-dot]'));
    var prev = root.querySelector('[data-gp-prev]');
    var next = root.querySelector('[data-gp-next]');
    var active = 0, onScreen = false, raf = 0;

    /* ---- arrival, and pausing off screen ---- */
    if (hasIO) {
      var live = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          root.classList.toggle('is-idle', !e.isIntersecting);
          if (e.isIntersecting && e.intersectionRatio > 0.12) root.classList.add('is-in');
        });
      }, { rootMargin: '80px 0px', threshold: [0, 0.12] });
      live.observe(root);
      cleanups.push(function () { live.disconnect(); });
    } else {
      root.classList.add('is-in');
    }

    if (!list) return;

    /* ---- phones: the carousel ---- */
    function centreOf(el) {
      var lr = list.getBoundingClientRect(), r = el.getBoundingClientRect();
      return r.left - lr.left + list.scrollLeft + r.width / 2;
    }
    function nearest() {
      var mid = list.scrollLeft + list.clientWidth / 2, best = 0, bestD = Infinity;
      items.forEach(function (el, i) { var d = Math.abs(centreOf(el) - mid); if (d < bestD) { bestD = d; best = i; } });
      return best;
    }
    function paint() {
      raf = 0;
      if (!phone.matches) {
        items.forEach(function (el) { el.classList.remove('is-active'); });
        return;
      }
      active = nearest();
      items.forEach(function (el, i) {
        el.classList.toggle('is-active', i === active);
        el.classList.toggle('is-pop', i === active && onScreen && !reduce);
      });
      dots.forEach(function (d, i) { if (i === active) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
      if (prev) prev.disabled = active === 0;
      if (next) next.disabled = active === items.length - 1;
    }
    function queue() { if (!raf) raf = requestAnimationFrame(paint); }
    function goTo(i) {
      i = Math.max(0, Math.min(items.length - 1, i));
      list.scrollTo({ left: centreOf(items[i]) - list.clientWidth / 2, behavior: reduce ? 'auto' : 'smooth' });
    }
    list.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    dots.forEach(function (d) { d.addEventListener('click', function () { goTo(+d.getAttribute('data-gp-dot')); }); });
    if (prev) prev.addEventListener('click', function () { goTo(active - 1); });
    if (next) next.addEventListener('click', function () { goTo(active + 1); });
    /* A focused card is scrolled to the centre, so tabbing walks the carousel. */
    list.addEventListener('focusin', function (e) {
      if (!phone.matches) return;
      var it = e.target.closest && e.target.closest('[data-gp-item]');
      if (it) goTo(items.indexOf(it));
    });

    /* A mouse can drag it; touch already scrolls natively. */
    var drag = null;
    list.addEventListener('pointerdown', function (e) {
      if (!phone.matches || e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, left: list.scrollLeft, moved: false };
    });
    window.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 6) { drag.moved = true; list.classList.add('is-dragging'); }
      if (drag.moved) list.scrollLeft = drag.left - dx;
    });
    window.addEventListener('pointerup', function () {
      if (!drag) return;
      var moved = drag.moved;
      drag = null;
      list.classList.remove('is-dragging');
      if (moved) {
        goTo(nearest());
        /* swallow the click that ends a drag, so dragging never opens a card */
        list.addEventListener('click', function stop(ev) { ev.preventDefault(); ev.stopPropagation(); }, { capture: true, once: true });
      }
    });
    list.addEventListener('dragstart', function (e) { if (phone.matches) e.preventDefault(); });

    /* The centred card pops only while the carousel is well on screen. */
    if (hasIO) {
      var seen = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { onScreen = e.intersectionRatio > 0.45; });
        queue();
      }, { threshold: [0, 0.45, 0.7] });
      seen.observe(list);
      cleanups.push(function () { seen.disconnect(); });
    }
    var onPhoneChange = function () {
      if (!phone.matches) items.forEach(function (el) { el.classList.remove('is-pop', 'is-active'); });
      queue();
    };
    if (phone.addEventListener) phone.addEventListener('change', onPhoneChange); else if (phone.addListener) phone.addListener(onPhoneChange);
    queue();

    /* ---- tablets without hover keep the grid. While a row crosses the middle of the
       screen, its games take turns: one card pops at a time, every few seconds, so each
       character gets its moment and only one card's effects ever run. ---- */
    if (noHover.matches && !reduce && hasIO) {
      var inBand = [], turn = 0, timer = 0, TURN_MS = 3200;
      var show = function () {
        items.forEach(function (el) { el.classList.remove('is-pop'); });
        if (phone.matches || !inBand.length) return;
        var ordered = items.filter(function (el) { return inBand.indexOf(el) > -1; });
        ordered[turn % ordered.length].classList.add('is-pop');
      };
      var stop = function () { clearInterval(timer); timer = 0; };
      var start = function () {
        stop();
        show();
        if (inBand.length > 1) timer = setInterval(function () { turn++; show(); }, TURN_MS);
      };
      var io = new IntersectionObserver(function (entries) {
        if (phone.matches) return;
        entries.forEach(function (e) {
          var i = inBand.indexOf(e.target);
          if (e.isIntersecting && i < 0) inBand.push(e.target);
          if (!e.isIntersecting && i > -1) inBand.splice(i, 1);
        });
        turn = 0;
        if (inBand.length) start(); else { stop(); show(); }
      }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
      items.forEach(function (el) { io.observe(el); });
      /* a tap goes straight to the card; no need to keep cycling behind it */
      document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else if (inBand.length) start(); });
      cleanups.push(function () { io.disconnect(); stop(); });
    }

    document.addEventListener('shopify:section:unload', function (e) {
      if (e.target && e.target.contains && e.target.contains(root)) cleanups.forEach(function (fn) { fn(); });
    });
  }

  function bootAll() { Array.prototype.forEach.call(document.querySelectorAll('[data-llgp]'), boot); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootAll);
  else bootAll();
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector && e.target.querySelector('[data-llgp]');
    if (el) boot(el);
  });
})();
